import React from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

interface PersonalInfoFieldsProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  disabled?: boolean;
  variant?: "billing" | "shipping";
}

const PersonalInfoFields: React.FC<PersonalInfoFieldsProps> = ({
  register,
  errors,
  watch,
  setValue,
  disabled = false,
  variant = "billing",
}) => {
  const { t } = useTranslation();

  // Feature Flag: Check if TCKN is enabled globally
  const enableTckn = window.commonVariables?.enableTckn ?? true;

  // Watch the customer type (Only relevant for Billing)
  const customerType = watch("customerType") || "individual";

  // Watch the country to conditionally show TCKN/VKN fields
  const country = watch("country");
  const isTurkey = country === "TR";

  const handleTypeChange = (type: "individual" | "corporate") => {
    if (disabled || variant === "shipping") return;

    // Simply switch the type. We preserve data in other fields (tckn, vkn, company)
    // so the user doesn't lose it if they switch back and forth.
    setValue("customerType", type);
  };

  // --- SHIPPING VIEW ---
  if (variant === "shipping") {
    return (
      <div
        className={`checkout-form__section ${disabled ? "is-disabled" : ""}`}
      >
        <h3 className="checkout-form__section-title">
          {t("checkoutForm.sections.personalInfo", "Personal Information")}
        </h3>
        <div className="checkout-form__grid">
          <div className="form-field">
            <label htmlFor="firstName" className="required">
              {t("checkoutForm.fields.recipientFirstName", "Recipient Name")}
            </label>
            <input
              id="firstName"
              {...register("firstName")}
              className={errors.firstName ? "has-error" : ""}
              disabled={disabled}
            />
            {errors.firstName && (
              <p className="form-field__error">
                {errors.firstName.message as string}
              </p>
            )}
          </div>
          <div className="form-field">
            <label htmlFor="lastName" className="required">
              {t("checkoutForm.fields.recipientLastName", "Recipient Surname")}
            </label>
            <input
              id="lastName"
              {...register("lastName")}
              className={errors.lastName ? "has-error" : ""}
              disabled={disabled}
            />
            {errors.lastName && (
              <p className="form-field__error">
                {errors.lastName.message as string}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- BILLING VIEW ---
  return (
    <div className={`checkout-form__section ${disabled ? "is-disabled" : ""}`}>
      <h3 className="checkout-form__section-title">
        {t("checkoutForm.sections.personalInfo", "Personal Information")}
      </h3>

      {/* Customer Type Toggle - Only show if NOT disabled */}
      {/* We allow the toggle globally to support Company Name for corporate clients, but TCKN/VKN logic is specific to TR */}
      {!disabled && (
        <div className="checkout-form__customer-type-toggle">
          <label
            className={`radio-label ${
              customerType === "individual" ? "is-active" : ""
            }`}
          >
            <input
              type="radio"
              value="individual"
              checked={customerType === "individual"}
              onChange={() => handleTypeChange("individual")}
              className="sr-only"
            />
            {t("checkoutForm.customerType.individual", "Individual")}
          </label>
          <label
            className={`radio-label ${
              customerType === "corporate" ? "is-active" : ""
            }`}
          >
            <input
              type="radio"
              value="corporate"
              checked={customerType === "corporate"}
              onChange={() => handleTypeChange("corporate")}
              className="sr-only"
            />
            {t("checkoutForm.customerType.corporate", "Corporate")}
          </label>
        </div>
      )}

      <div className="checkout-form__grid">
        {/* First Name */}
        <div className="form-field">
          <label htmlFor="firstName" className="required">
            {customerType === "corporate"
              ? t(
                  "checkoutForm.fields.authorizedFirstName",
                  "Authorized Person Name"
                )
              : t("checkoutForm.fields.firstName", "First Name")}
          </label>
          <input
            id="firstName"
            {...register("firstName")}
            className={errors.firstName ? "has-error" : ""}
            disabled={disabled}
          />
          {errors.firstName && (
            <p className="form-field__error">
              {errors.firstName.message as string}
            </p>
          )}
        </div>

        {/* Last Name */}
        <div className="form-field">
          <label htmlFor="lastName" className="required">
            {customerType === "corporate"
              ? t(
                  "checkoutForm.fields.authorizedLastName",
                  "Authorized Person Surname"
                )
              : t("checkoutForm.fields.lastName", "Last Name")}
          </label>
          <input
            id="lastName"
            {...register("lastName")}
            className={errors.lastName ? "has-error" : ""}
            disabled={disabled}
          />
          {errors.lastName && (
            <p className="form-field__error">
              {errors.lastName.message as string}
            </p>
          )}
        </div>
      </div>

      {/* CONDITIONAL FIELDS */}
      {!disabled && (
        <div className="checkout-form__grid">
          {customerType === "individual" ? (
            // --- INDIVIDUAL: TCKN Only (Turkey Specific & Enabled) ---
            // Added KEY to prevent React from reusing this DOM node for the Company field
            isTurkey &&
            enableTckn && (
              <div className="form-field" key="field-tckn">
                <label htmlFor="tckn">
                  {t(
                    "checkoutForm.fields.tckn",
                    "TC Identity Number (Optional)"
                  )}
                </label>
                <input
                  id="tckn"
                  {...register("tckn")}
                  className={errors.tckn ? "has-error" : ""}
                  maxLength={11}
                  placeholder={t("checkoutForm.placeholders.tckn", "11 digits")}
                />
                {errors.tckn && (
                  <p className="form-field__error">
                    {errors.tckn.message as string}
                  </p>
                )}
              </div>
            )
          ) : (
            // --- CORPORATE: Company Title + VKN (Turkey Specific) ---
            // Added KEY to ensure these fields are treated as new elements
            <React.Fragment key="fields-corporate">
              <div className="form-field">
                <label htmlFor="company" className="required">
                  {t(
                    "checkoutForm.fields.companyTitle",
                    "Company Title (Ünvan)"
                  )}
                </label>
                <input
                  id="company"
                  {...register("company")}
                  className={errors.company ? "has-error" : ""}
                />
                {errors.company && (
                  <p className="form-field__error">
                    {errors.company.message as string}
                  </p>
                )}
              </div>

              {isTurkey && (
                <div className="form-field">
                  <label htmlFor="vkn" className="required">
                    {t("checkoutForm.fields.vkn", "Tax ID Number (VKN)")}
                  </label>
                  <input
                    id="vkn"
                    {...register("vkn")}
                    className={errors.vkn ? "has-error" : ""}
                    maxLength={10}
                  />
                  {errors.vkn && (
                    <p className="form-field__error">
                      {errors.vkn.message as string}
                    </p>
                  )}
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      )}
    </div>
  );
};

export default PersonalInfoFields;
